import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import IconClose from 'components/icons/iconClose';

const STORE_TYPE_SHOWROOM = 1;
const STORE_TYPE_PARTNER = 2;

const StoreLink = ({ storeData, isPartner = false }) => {
  const slug =
    storeData.slug?.replace(/^\/+|\/+$/g, '')?.replace(/^showrooms\//, '') ||
    '';
  const showroomUrl = slug ? `/showrooms/${slug}/` : '/showrooms/';
  return (
    <div className="text-sm ml-2">
      <Link
        to={showroomUrl}
        className={`underline hover:no-underline !font-bold ${
          isPartner ? 'text-gray' : 'text-brand'
        }`}
      >
        {storeData.city}, {storeData.state}
      </Link>
    </div>
  );
};

const StoreSection = ({ title, stores, isPartner = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (stores.length === 0) return null;

  const hasMultiple = stores.length > 1;
  const additionalCount = stores.length - 1;
  const storesToShow = isExpanded ? stores : stores.slice(0, 1);

  return (
    <div className={isPartner ? 'mt-3' : ''}>
      <div className="text-sm text-gray mb-1">{title}:</div>
      {storesToShow.map(store => (
        <StoreLink key={store.storeData.id} {...store} isPartner={isPartner} />
      ))}
      {hasMultiple && !isExpanded && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className={`text-sm underline hover:no-underline mt-1 ml-2 !font-bold ${
            isPartner ? 'text-gray' : 'text-brand'
          }`}
        >
          + {additionalCount} more
        </button>
      )}
    </div>
  );
};

StoreSection.propTypes = {
  title: PropTypes.string.isRequired,
  stores: PropTypes.array.isRequired,
  isPartner: PropTypes.bool,
};

StoreSection.defaultProps = {
  isPartner: false,
};

const ShowroomLocatorResults = ({
  loading,
  error,
  stores,
  onClose,
  isMobile,
}) => {
  if (loading) {
    return <div className="text-sm text-gray mt-2">Searching...</div>;
  }

  if (error) {
    return (
      <div className="mt-2">
        <div className="text-sm text-red mb-2">
          Error searching for showroom. Please try again.
        </div>
      </div>
    );
  }

  const validStores = stores.filter(store => store.storeData);
  const showrooms = validStores.filter(
    store => store.storeData.type === STORE_TYPE_SHOWROOM
  );
  const partnerStudios = validStores.filter(
    store => store.storeData.type === STORE_TYPE_PARTNER
  );

  const hasAnyResults = showrooms.length > 0 || partnerStudios.length > 0;

  if (hasAnyResults) {
    return (
      <div
        className={`mt-2 ${
          isMobile ? '' : 'pt-2 [border-top:1px_solid_#f1f1f1]'
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <StoreSection title="Showrooms" stores={showrooms} />
            <StoreSection
              title="Partner Studios"
              stores={partnerStudios}
              isPartner
            />
          </div>
          {!isMobile && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray pt-2 pr-1"
              aria-label="Close results"
            >
              <IconClose width={10} height={10} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mt-2 ${isMobile ? '' : 'pt-2 border-t border-t-gray-light5'}`}
    >
      <div className="flex justify-between items-start">
        <div className="text-sm text-gray mb-1">Showroom results:</div>
        {!isMobile && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-gray pt-2 pr-1"
            aria-label="Close results"
          >
            <IconClose width={10} height={10} />
          </button>
        )}
      </div>
      <div className="text-sm text-red mb-1 pl-2">
        No showrooms within range
      </div>
      <div className="text-sm text-gray pl-2">
        Try our{' '}
        <Link to="/showrooms/virtual/" className="text-brand underline">
          Virtual Showroom
        </Link>
      </div>
    </div>
  );
};

ShowroomLocatorResults.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  stores: PropTypes.array,
  onClose: PropTypes.func,
  isMobile: PropTypes.bool,
};

ShowroomLocatorResults.defaultProps = {
  stores: [],
  onClose: null,
  isMobile: false,
};

export default ShowroomLocatorResults;
